import { useCallback, useEffect, useMemo, useRef } from "react";
import { buildReceiverReadyMessage, parseTextMessage } from "../../localTransfer/utils/transferProtocol";
import { closePeerConnection, sendChannelMessage } from "../../localTransfer/utils/webrtc";
import { createIncomingTransfer, readReceiverFileChunk, readReceiverTextMessage } from "../../localTransfer/utils/receiveTransfer";
import { sendLocalTransfer } from "../../localTransfer/utils/sendTransfer";
import { createPairingCode } from "../../localTransfer/utils/pairingCode";
import { TRANSFER_MESSAGE_TYPES } from "../../localTransfer/localTransfer.constants";
import { NEARBY_TRANSFER_ERROR_MESSAGE, WEBRTC_SIGNAL_KINDS } from "../nearbyTransfer.constants";
import { NEARBY_TRANSFER_ACTIONS } from "../nearbyTransfer.reducer";
import { createWebRtcSignalMessage } from "../utils/messages";
import { addRemoteIceCandidate, applyAnswerDescription, createReceiverPeer, createRequesterPeer } from "../utils/nearbyPeerConnection";
import { buildNearbyErrorMessage } from "../utils/errorMessage";

export const useNearbyPeerSession = ({
  dispatch,
  onFailure,
  sendSocketMessage,
  transferPayload,
}) => {
  const peerConnectionRef = useRef(null);
  const channelRef = useRef(null);
  const requestRef = useRef(null);
  const offerDescriptionRef = useRef(null);
  const pendingCandidatesRef = useRef([]);
  const pairingCodeRef = useRef("");
  const transferPayloadRef = useRef(transferPayload);
  const incomingTransferRef = useRef(createIncomingTransfer());
  const isManualCloseRef = useRef(false);

  useEffect(() => {
    transferPayloadRef.current = transferPayload;
  }, [transferPayload]);

  const closePeerSession = useCallback(() => {
    isManualCloseRef.current = Boolean(channelRef.current);
    channelRef.current?.close();
    channelRef.current = null;
    closePeerConnection(peerConnectionRef.current);
    peerConnectionRef.current = null;
    requestRef.current = null;
    offerDescriptionRef.current = null;
    pendingCandidatesRef.current = [];
    pairingCodeRef.current = "";
    incomingTransferRef.current = createIncomingTransfer();
  }, []);

  const fail = useCallback(
    (errorMessage = NEARBY_TRANSFER_ERROR_MESSAGE) => {
      closePeerSession();
      onFailure(errorMessage);
    },
    [closePeerSession, onFailure]
  );

  const sendSignal = useCallback(
    (signal) => {
      const request = requestRef.current;
      if (!request) return;

      sendSocketMessage(
        createWebRtcSignalMessage({
          sessionId: request.sessionId,
          targetDeviceId: request.targetDeviceId,
          signal,
        })
      );
    },
    [sendSocketMessage]
  );

  const flushPendingCandidates = useCallback(async () => {
    const peerConnection = peerConnectionRef.current;
    if (!peerConnection) return;

    const pendingCandidates = pendingCandidatesRef.current;
    pendingCandidatesRef.current = [];
    await Promise.all(pendingCandidates.map((candidate) =>
      addRemoteIceCandidate({ peerConnection, candidate })
    ));
  }, []);

  const setConnected = useCallback(() => {
    dispatch({
      type: NEARBY_TRANSFER_ACTIONS.SET_CONNECTED,
      connectedDevice: requestRef.current?.device,
      pairingCode: pairingCodeRef.current,
    });
  }, [dispatch]);

  const sendPeerReady = useCallback(async () => {
    const channel = channelRef.current;
    if (channel?.readyState !== "open") return;

    await sendChannelMessage({ channel, message: buildReceiverReadyMessage() });
  }, []);

  const handleIncomingText = useCallback(
    (messageText) => {
      const transferResult = readReceiverTextMessage({
        messageText,
        incomingTransfer: incomingTransferRef.current,
      });
      if (transferResult.incomingTransfer) {
        incomingTransferRef.current = transferResult.incomingTransfer;
        dispatch({ type: NEARBY_TRANSFER_ACTIONS.SET_TRANSFER_PROGRESS, transferProgress: 0 });
      }
      if (transferResult.receivedTransfer) {
        dispatch({ type: NEARBY_TRANSFER_ACTIONS.SET_RECEIVED_TRANSFER, receivedTransfer: transferResult.receivedTransfer });
      }
      if (transferResult.errorMessage) fail(transferResult.errorMessage);
    },
    [dispatch, fail]
  );

  const handleTextMessage = useCallback(
    (messageText) => {
      const message = parseTextMessage(messageText);
      if (message.type === TRANSFER_MESSAGE_TYPES.RECEIVER_READY) {
        dispatch({ type: NEARBY_TRANSFER_ACTIONS.SET_PEER_READY });
        return;
      }

      handleIncomingText(messageText);
    },
    [dispatch, handleIncomingText]
  );

  const handleFileChunk = useCallback(
    (messageBuffer) => {
      const transferResult = readReceiverFileChunk({
        messageBuffer,
        incomingTransfer: incomingTransferRef.current,
      });
      incomingTransferRef.current = transferResult.incomingTransfer;
      dispatch({ type: NEARBY_TRANSFER_ACTIONS.SET_TRANSFER_PROGRESS, transferProgress: transferResult.transferProgress });
    },
    [dispatch]
  );

  const handleChannelMessage = useCallback(
    (event) => {
      try {
        if (typeof event.data === "string") {
          handleTextMessage(event.data);
          return;
        }

        handleFileChunk(event.data);
      } catch (error) {
        fail(buildNearbyErrorMessage({ action: "Reading nearby transfer message", error }));
      }
    },
    [fail, handleFileChunk, handleTextMessage]
  );

  const handleChannelClose = useCallback(() => {
    if (isManualCloseRef.current) {
      isManualCloseRef.current = false;
      return;
    }

    closePeerSession();
    dispatch({ type: NEARBY_TRANSFER_ACTIONS.DISCONNECT });
  }, [closePeerSession, dispatch]);

  const setupChannel = useCallback(
    (channel) => {
      channel.binaryType = "arraybuffer";
      channelRef.current = channel;
      channel.onopen = async () => {
        setConnected();
        await sendPeerReady();
      };
      channel.onmessage = handleChannelMessage;
      channel.onerror = () => fail();
      channel.onclose = handleChannelClose;
    },
    [fail, handleChannelClose, handleChannelMessage, sendPeerReady, setConnected]
  );

  const startOffer = useCallback(async () => {
    try {
      dispatch({ type: NEARBY_TRANSFER_ACTIONS.PAIRING });
      const peer = await createRequesterPeer({ onChannel: setupChannel, onSignal: sendSignal });
      peerConnectionRef.current = peer.peerConnection;
      offerDescriptionRef.current = peer.offerDescription;
    } catch (error) {
      fail(buildNearbyErrorMessage({ action: "Creating WebRTC offer", error }));
    }
  }, [dispatch, fail, sendSignal, setupChannel]);

  const receiveOffer = useCallback(
    async (offerDescription) => {
      try {
        const peer = await createReceiverPeer({ offerDescription, onChannel: setupChannel, onSignal: sendSignal });
        peerConnectionRef.current = peer.peerConnection;
        offerDescriptionRef.current = offerDescription;
        pairingCodeRef.current = await createPairingCode({
          offerDescription,
          answerDescription: peer.answerDescription,
        });
        dispatch({ type: NEARBY_TRANSFER_ACTIONS.SET_PAIRING_CODE, pairingCode: pairingCodeRef.current });
        await flushPendingCandidates();
      } catch (error) {
        fail(buildNearbyErrorMessage({ action: "Creating WebRTC answer", error }));
      }
    },
    [dispatch, fail, flushPendingCandidates, sendSignal, setupChannel]
  );

  const receiveAnswer = useCallback(
    async (answerDescription) => {
      try {
        await applyAnswerDescription({ peerConnection: peerConnectionRef.current, description: answerDescription });
        pairingCodeRef.current = await createPairingCode({
          offerDescription: offerDescriptionRef.current,
          answerDescription,
        });
        dispatch({ type: NEARBY_TRANSFER_ACTIONS.SET_PAIRING_CODE, pairingCode: pairingCodeRef.current });
        await flushPendingCandidates();
      } catch (error) {
        fail(buildNearbyErrorMessage({ action: "Applying WebRTC answer", error }));
      }
    },
    [dispatch, fail, flushPendingCandidates]
  );

  const receiveCandidate = useCallback(async (candidate) => {
    const peerConnection = peerConnectionRef.current;
    if (!peerConnection) {
      pendingCandidatesRef.current = [...pendingCandidatesRef.current, candidate];
      return;
    }

    try {
      await addRemoteIceCandidate({ peerConnection, candidate });
    } catch (error) {
      fail(buildNearbyErrorMessage({ action: "Adding WebRTC ICE candidate", error }));
    }
  }, [fail]);

  const isExpectedSignal = useCallback((message) => {
    const request = requestRef.current;
    return (
      request?.sessionId === message.sessionId &&
      request?.targetDeviceId === message.fromDeviceId
    );
  }, []);

  const receiveSignal = useCallback(
    async (message) => {
      if (!isExpectedSignal(message)) return;

      const { signal } = message;
      if (signal?.kind === WEBRTC_SIGNAL_KINDS.OFFER) {
        await receiveOffer(signal.description);
      }
      if (signal?.kind === WEBRTC_SIGNAL_KINDS.ANSWER) {
        await receiveAnswer(signal.description);
      }
      if (signal?.kind === WEBRTC_SIGNAL_KINDS.ICE_CANDIDATE) {
        await receiveCandidate(signal.candidate);
      }
    },
    [isExpectedSignal, receiveAnswer, receiveCandidate, receiveOffer]
  );

  const sendCurrentTransfer = useCallback(async () => {
    const channel = channelRef.current;
    if (channel?.readyState !== "open") return fail("Nearby device is not connected.");

    try {
      dispatch({ type: NEARBY_TRANSFER_ACTIONS.SET_TRANSFER_PROGRESS, transferProgress: 0 });
      await sendLocalTransfer({
        channel,
        payload: transferPayloadRef.current,
        onProgress: (transferProgress) => dispatch({
          type: NEARBY_TRANSFER_ACTIONS.SET_TRANSFER_PROGRESS,
          transferProgress,
        }),
      });
      setConnected();
    } catch (error) {
      fail(buildNearbyErrorMessage({ action: "Sending nearby transfer", error }));
    }
  }, [dispatch, fail, setConnected]);

  const setPairRequest = useCallback((pairRequest) => {
    requestRef.current = pairRequest;
  }, []);

  return useMemo(
    () => ({ closePeerSession, receiveSignal, sendCurrentTransfer, setPairRequest, startOffer }),
    [closePeerSession, receiveSignal, sendCurrentTransfer, setPairRequest, startOffer]
  );
};
